import Sparkline from "src/components/charts/Sparkline";
import React, { useState } from "react";
import { Empty, message, Skeleton, Tooltip } from "antd";
import styled from "styled-components";
import {
  goalBasedApi,
  useGetSelfMadeQuery,
} from "src/store/Goal-Based/goalBasedApi";
import { SmartfolioBuilder } from "../components/SmartfolioBuilder/SmartfolioBuilder";
import { useDispatch, useSelector } from "react-redux";
import { RootStateType } from "src/store/rootReducer";
import { useHistory } from "react-router";
import RadioList from "../components/RadioOptions";
import { useGetRecommendedSmartfoliosQuery } from "src/store/middleware/api_v2";
import { sparkSpan } from "src/components/charts/Sparkline";
import NotYetValued from "src/components/charts/NotYetValued";
import { colors } from "src/theme/colors";
import { line, radii, space, text, withAlpha } from "src/theme/ui";
import { Button as UIButton } from "src/components/ui";
import { setSmartfolioSelected } from "src/store/Goal-Based/MenuSlice";

interface SmartfolioCardProps {
  display_name: string;
  description: string;
  id?: number;
  nav?: { [date: string]: number };
  source?: string;
  onSelectSmartfolio: (smartfolioId: number) => void;
  selectedSmartfolioId?: number;
}
export const StyledBadge = styled.span<{ $pending?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
  padding: 1px 8px;
  border-radius: ${radii.pill}px;
  user-select: none;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.02em;
  border: 1px solid
    ${({ $pending }) => ($pending ? "rgba(242, 152, 17, 0.28)" : line.hairline)};
  background: ${({ $pending }) =>
    $pending ? "rgba(242, 152, 17, 0.10)" : colors.gray[50]};
  color: ${({ $pending }) => ($pending ? "#B26A05" : colors.gray[600])};
`;

interface Props {
  name: string;
  /** Whether the smartfolio is still being built. */
  pending?: boolean;
}

/**
 * Who made it, and whether it is ready.
 *
 * These were the same words in two colours: "Based on FinaMaze Capital
 * Smartfolio" in orange when the smartfolio was still being created and in
 * accent when it was ready. So the one fact that changes anything for the
 * reader — that a row cannot be used yet — was carried by hue alone, on a
 * label whose text said something else entirely. A reader who cannot
 * separate those two hues, or who simply is not looking for a colour
 * difference between two identical strings, got nothing.
 *
 * Pending says so in words. Ready says who made it, quietly, because
 * provenance is context and not a warning.
 */
export function TitleBadge({ name, pending }: Props) {
  return (
    <StyledBadge $pending={pending}>
      {pending ? "Being created" : `By ${name}`}
    </StyledBadge>
  );
}

export const ConditionalWrap = ({ condition, wrap, children }) =>
  condition ? wrap(children) : children;

export const CustomTinyLineText = styled.p`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #929fac;
  @media only screen and (max-width: 767px) {
    display: none;
  }
`;

const StyledText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

/* Ink, not a link. It was an antd Link — accent blue and underlined on
   hover — inside a card that is itself the click target, so the row offered
   two things to click and only one of them did anything. */
const CardName = styled.span`
  font-size: 14.5px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${colors.gray[900]};
`;

const CardDescription = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: ${colors.gray[500]};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SmartfolioCard: React.FC<SmartfolioCardProps> = ({
  display_name,
  description,
  nav,
  id,
  source,
  onSelectSmartfolio,
  selectedSmartfolioId,
}) => {
  const history = useHistory();

  const { currentState: selectedMenu } = useSelector(
    (state: RootStateType) => state.menuReducer,
  );
  const input = useSelector(
    (state: RootStateType) => state[`${selectedMenu}Reducer`],
  );

  const [getPlan] = goalBasedApi[`useLazyGet${selectedMenu}StatsQuery`]();

  const [isMessageOpen, setIsMessageOpen] = useState(false);

  const showMessage = () => {
    if (!isMessageOpen) {
      setIsMessageOpen(true);
      message.info("Please enter initial amount!", 2, () => {
        setIsMessageOpen(false);
      });
    }
  };

  return (
    <ConditionalWrap
      condition={!nav}
      wrap={(wrappedChildren) => (
        <Tooltip title="We're currently in the creation phase.Please stay tuned, and we'll notify you as soon as it's ready for you to explore.">
          {wrappedChildren}
        </Tooltip>
      )}
    >
      <StyledCard
        blocked={false}
        $selected={selectedSmartfolioId === id}
        onClick={async (e) => {
          // if (input.initial_amount === "0") showMessage();
          // else {
          e.stopPropagation();
          const res = await getPlan({ input, option: "true", id }).unwrap();
          window.location.href = `goal-predictions/${res?.planId}`;
          // }
        }}
      >
        <StyledText>
          <CardHead>
            <CardName>{display_name}</CardName>
            {source && source !== "USER-DEFINED" ? (
              <TitleBadge
                pending={!nav}
                name={`${process.env.REACT_APP_BANK_NAME || ""} Capital`}
              />
            ) : null}
          </CardHead>
          {/* Two lines, then an ellipsis. These run to forty words, and eight
              of them at full length made a list nobody could scan — the name
              of the next row was always off the bottom of the pane. */}
          <CardDescription>{description}</CardDescription>
        </StyledText>

        {!!nav ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* The period the series covers, read off its own dates — the
                field it arrives in is called spark_line_12_month and holds
                a month of weekly readings. See sparkSpan. */}
            <CustomTinyLineText>{sparkSpan(nav) ?? ""}</CustomTinyLineText>
            <TinyLineChart data={Object.values(nav)} />
          </div>
        ) : (
          /* It showed the glyph alone here, with nothing to say what it
             meant. Same state, same words, as the smartfolio list. */
          <div style={{ paddingRight: "1rem" }}>
            <NotYetValued />
          </div>
        )}
      </StyledCard>
    </ConditionalWrap>
  );
};

const CustomSkeleton = styled(Skeleton)`
  .ant-skeleton-title {
    margin: 0;
  }
  .ant-skeleton-paragraph {
    margin: 0;
  }
`;

const SkeletonItem = () => {
  return (
    <StyledSkeleton>
      <CustomSkeleton active paragraph={{ rows: 1 }} />
    </StyledSkeleton>
  );
};

const SkeletonList = () => {
  return (
    <>
      <SkeletonItem />
      <SkeletonItem />
    </>
  );
};

/**
 * The label over a run of cards.
 *
 * The same treatment the Markets page uses over its runs, and the one this
 * page's own chooser now opens with — so a group of cards is announced the
 * same way everywhere in the product. These were 27px level-2 headings:
 * larger than anything else on the page, including the answer the page
 * exists to give.
 */
const SectionHeading = styled.h2`
  ${text.label}
  margin: 0 0 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 10.5px;
  color: ${colors.gray[500]};
`;

const HeadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space.md}px;
  margin-bottom: 10px;

  ${SectionHeading} {
    margin-bottom: 0;
  }
`;

const StyledSmartfoliosSection = styled.div`
  display: flex;
  gap: 1.2rem;
  flex-direction: row;
  @media (max-width: 2000px) {
    flex-direction: column;
  }
`;

const options = [
  { label: "Retirement Lump Sum", value: "retirementLumpSum" },
  { label: "Retirement Monthly Income", value: "retirementMonthlyIncome" },
  { label: "Short term", value: "shortTerm" },
  { label: "Property", value: "property" },
  { label: "Business", value: "business" },
  { label: "Education", value: "education" },
  { label: "Debt", value: "debt" },
  { label: "Inheritence", value: "inheritance" },
  { label: "No specific goal", value: "none" },
];

const SmartfolioList: React.FC = () => {
  const { data, isFetching, refetch } = useGetRecommendedSmartfoliosQuery({
    page_size: 100,
    page_number: 1,
    section: "GOAL-BASED-INVESTMENT",
  });

  const {
    data: selfMade,
    isLoading: selfMadeLoading,
    isFetching: selfMadeFetching,
  } = useGetSelfMadeQuery();
  const [visible, setVisible] = useState(false);

  const [selectedSmartfolio, setSelectedSmartfolio] = useState<number | null>(
    null,
  );
  const [planId, setPlanId] = useState<any>();

  const dispatch = useDispatch();

  const handleSelect = (smartfolioId: number) => {
    setSelectedSmartfolio(smartfolioId);
    dispatch(setSmartfolioSelected({ isSmartfolioSelected: true }));
  };

  const { currentState: selectedMenu } = useSelector(
    (state: RootStateType) => state.menuReducer,
  );

  const input = useSelector(
    (state: RootStateType) => state[`${selectedMenu}Reducer`],
  );

  const [getPlan] = goalBasedApi[`useLazyGet${selectedMenu}StatsQuery`]();

  const [confirmationPopupVisible, setConfirmationPopupVisible] =
    useState(false);

  const openConfirmationPopup = (value: boolean) => {
    setConfirmationPopupVisible(value);
  };

  return (
    <>
      <StyledSmartfoliosSection>
        <div style={{ flexBasis: "50%" }}>
          <SmartfolioBuilder visible={visible} setVisible={setVisible} />
          <SectionHeading>Objective</SectionHeading>
          <RadioList options={options} refetch={refetch} />
          <SectionHeading>Recommended Smartfolios</SectionHeading>
          <StyledSmartfolioList>
            {isFetching ? (
              <SkeletonList />
            ) : data?.count === 0 ? (
              <Empty />
            ) : (
              data?.data
                ?.filter(
                  (item) => item?.object_content?.display_name != undefined,
                )
                .map(({ id, object_content }) => {
                  const { display_name, description, performances } =
                    object_content;
                  return (
                    <SmartfolioCard
                      id={object_content.id}
                      key={id}
                      display_name={display_name}
                      description={description}
                      nav={performances?.spark_line_12_month}
                      onSelectSmartfolio={handleSelect}
                      selectedSmartfolioId={selectedSmartfolio}
                    />
                  );
                })
            )}
          </StyledSmartfolioList>
        </div>
        <div style={{ flexBasis: "50%" }}>
          <HeadingRow>
            <SectionHeading>Self-made</SectionHeading>
            {/* A button, because it opens a builder. It had been a heading
                the same size and weight as the section title beside it,
                coloured accent and given a click handler — so the row read
                as two headings, one of which happened to do something. */}
            <UIButton
              variant="secondary"
              size="sm"
              onClick={() => setVisible(true)}
            >
              Build your own
            </UIButton>
          </HeadingRow>
          <StyledSmartfolioList>
            {selfMadeFetching ? (
              <SkeletonList />
            ) : selfMade?.count === 0 ? (
              <Empty />
            ) : (
              // The smartfolio owns the description now that editing writes it
              // there; the basket copy is only a fallback for rows last edited
              // before that, which never had the smartfolio-level one written.
              selfMade?.data?.map((smartfolio) => (
                <SmartfolioCard
                  id={smartfolio?.id}
                  key={smartfolio?.id}
                  display_name={smartfolio?.display_name}
                  description={
                    smartfolio?.description ||
                    smartfolio?.strategy?.basket?.description
                  }
                  nav={smartfolio?.performances.spark_line_12_month}
                  source={smartfolio?.source}
                  onSelectSmartfolio={setSelectedSmartfolio}
                  selectedSmartfolioId={selectedSmartfolio}
                />
              ))
            )}
          </StyledSmartfolioList>
        </div>
      </StyledSmartfoliosSection>
    </>
  );
};

/**
 * One recommendation.
 *
 * The product's card — white, hairline, soft shadow — rather than a
 * periwinkle panel that turned a slightly different periwinkle when
 * selected. On this tenant `primary[10]` and `primary[25]` are full colours
 * from the brand ramp, not tints, so a list of eight of them was the loudest
 * thing on a page whose actual subject is a form; and selection was a change
 * between two of those fills, which is the hardest kind of difference to see
 * in a scrolling list.
 *
 * Selected is now an accent EDGE against unselected white: a difference in
 * the object's outline rather than in the shade of its middle.
 *
 * It also set `color: #0000` — transparent text. Every child happened to set
 * its own colour, so nothing was visibly missing, but any text added to this
 * card would have been invisible.
 */
const StyledCard = styled.div<{ $selected?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${space.md}px;
  margin-bottom: 10px;
  padding: ${space.md}px;
  border-radius: ${radii.lg}px;
  border: 1px solid
    ${({ $selected }) => ($selected ? colors.primary[500] : line.hairline)};
  background: ${({ $selected }) =>
    $selected ? withAlpha(colors.primary[500], 0.05) : colors.white};
  color: ${colors.gray[900]};
  cursor: ${({ blocked }) => (blocked ? "wait" : "pointer")};
  transition: border-color 0.15s ease, background 0.15s ease;

  /* No shadow, and none on hover either.
     These cards live in a scrolling pane, and an overflow container clips a
     shadow mid-blur — so every row showed a hard grey band across its foot
     where the blur was cut off, and the band was widest at the two sides
     where the spread had nowhere to go. Eight stacked shadows in a dense
     list are noise rather than depth in any case; the hairline already
     bounds the card, so hover and selection are said by the edge. */
  &:hover {
    border-color: ${({ $selected }) =>
      $selected ? colors.primary[500] : colors.gray[400]};
    background: ${({ $selected }) =>
      $selected ? withAlpha(colors.primary[500], 0.05) : colors.gray[25]};
  }
`;

const StyledSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  background: ${colors.gray[50]};
  border: 1px solid ${line.hairline};
  padding: 1em;
  border-radius: ${radii.lg}px;
  min-height: 6rem;
`;

const ClickableDottedArea = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 0;
  justify-content: center;
  background: ${colors.gray[25]};
  border: 1px dashed ${colors.gray[300]};
  cursor: pointer;
  border-radius: ${radii.lg}px;
  padding: 1em;
  height: 9em;
  margin-bottom: 1rem;
`;

const StyledSmartfolioList = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  max-height: 40rem;

  /* Hide the scrollbar by default */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    width: 0;
  }

  /* Show the scrollbar when hovering over the container */
  &:hover {
    scrollbar-width: thin;
    -ms-overflow-style: auto;
    &::-webkit-scrollbar {
      width: 8px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: rgba(0, 0, 0, 0.3);
      border-radius: 4px;
    }
    &::-webkit-scrollbar-track {
      background-color: rgba(0, 0, 0, 0.1);
      border-radius: 4px;
    }
  }
`;

/**
 * Twelve months of NAV for one smartfolio.
 *
 * The product's shared spark line, not the charting library's TinyArea. That
 * one reserves a gutter of its own, so 150px of chart drew in about ninety;
 * its colours had to be spelled out as gradient strings, and this copy of
 * them carried `1:#f0443826` in two of its four slots — a stop index where a
 * colour belongs — so the falling case never got the wash it was written to
 * have; and it smoothed between readings, which on a NAV series draws values
 * the holding never had.
 */
const TinyLineChart: React.FC<{ data: number[] }> = ({ data }) => (
  <Sparkline values={data} width={116} height={36} />
);

export default SmartfolioList;
